import { Box, HStack, Image, Pressable, Text, VStack } from "native-base";
import Footer from "../components/Footer";
import { getSecurityParagraphs } from "../helper/security.helper";
import { useTranslation } from 'react-i18next';
import Navbar from "../components/Navbar";
import { Link } from "react-router-dom";
import React from "react";

const renderTextWithLinks = (text: string) => {
    // Regex for URLs (http://, https://, www., or domain patterns)
    // Improved to detect domains like track.a2a56.com
    // Pattern: (protocol://) | www. | domain with subdomains
    const urlRegex = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+|[a-zA-Z0-9][a-zA-Z0-9.-]*[a-zA-Z0-9]\.[a-zA-Z]{2,}(?:\/[^\s<>"']*)?)/gi;
    // Regex for emails
    const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi;
    
    const matches: Array<{index: number, length: number, type: 'url' | 'email', text: string, href?: string}> = [];
    let match;
    let key = 0;
    
    // Find all URLs
    urlRegex.lastIndex = 0;
    while ((match = urlRegex.exec(text)) !== null) {
        let url = match[0];
        // Remove trailing punctuation that might have been captured
        url = url.replace(/[.,;:!?]+$/, '');
        
        // Ensure URL has protocol
        const href = url.startsWith('www.') ? `https://${url}` : 
                     url.startsWith('http://') || url.startsWith('https://') ? url : 
                     `https://${url}`;
        matches.push({
            index: match.index,
            length: url.length,
            type: 'url',
            text: url,
            href: href
        });
    }
    
    // Find all emails
    emailRegex.lastIndex = 0;
    while ((match = emailRegex.exec(text)) !== null) {
        matches.push({
            index: match.index,
            length: match[0].length,
            type: 'email',
            text: match[0]
        });
    }
    
    // Sort matches by index
    matches.sort((a, b) => a.index - b.index);
    
    // Remove overlapping matches (prioritize emails over URLs)
    const filteredMatches: typeof matches = [];
    for (let i = 0; i < matches.length; i++) {
        const current = matches[i];
        let overlaps = false;
        
        for (let j = 0; j < filteredMatches.length; j++) {
            const existing = filteredMatches[j];
            if (current.index < existing.index + existing.length && 
                current.index + current.length > existing.index) {
                overlaps = true;
                // If current is email and existing is URL, replace it
                if (current.type === 'email' && existing.type === 'url') {
                    filteredMatches[j] = current;
                }
                break;
            }
        }
        
        if (!overlaps) {
            filteredMatches.push(current);
        }
    }
    
    // Build parts array
    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    
    filteredMatches.forEach((match) => {
        // Add text before the match
        if (match.index > lastIndex) {
            parts.push(text.substring(lastIndex, match.index));
        }
        
        // Add the link
        if (match.type === 'email') {
            parts.push(
                <a key={key++} href={`mailto:${match.text}`} style={{ color: '#1264A3', textDecoration: 'underline', cursor: 'pointer' }}>
                    {match.text}
                </a>
            );
        } else {
            const href = match.href || match.text;
            parts.push(
                <a key={key++} href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#1264A3', textDecoration: 'underline', cursor: 'pointer' }}>
                    {match.text}
                </a>
            );
        }
        
        lastIndex = match.index + match.length;
    });
    
    // Add remaining text
    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 0 ? parts : [text];
};

export const SecurityPolicy = () => {
    const { t } = useTranslation();
    const paragraphs = getSecurityParagraphs(t);
    return (
        <Box>
            <div className="landing_container landing_gradient" style={{ zIndex: 3, height: '296px' }}>
                <Navbar upper={true} />
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <Box top="50px">
                        <Text color="#ffb31c" textAlign="left" fontStyle="italic" fontWeight={800} fontSize={window.innerWidth < 1920 ? "53px" : "60px"} fontFamily="Segoe UI">{t('Privacy Policy')}</Text>
                    </Box>
                </div>
                <div style={{ top: '170px', right: "40px", position: 'absolute', width: '210px', height: '228px' }}>
                    <Image
                        width={'210px'}
                        height={'228px'}
                        style={{ position: 'absolute' }}
                        resizeMode={'cover'}
                        source={{
                            uri: `${process.env.PUBLIC_URL}/icons/help/lock.svg`,
                        }}
                    />
                </div>
            </div>
            <Box w="100%" display="flex" justifyContent="center" flexDir="row" mt="20px">
                <HStack px={'auto'} width="70%">
                    <VStack width="50%">
                        <VStack>
                            <Text bold fontSize="25px" color="#293d8b" italic fontFamily="Segoe UI">{t('table_of_contents')}</Text>
                            <Box maxWidth="100px" width="100%" height="3px" bg={'#ff9300'} />
                        </VStack>
                        {paragraphs.filter(el => el.type === "title").map((el, i) => {
                            return <Link key={i} to={`#${el.text}`} style={{ textDecoration: 'none' }}>
                                <Pressable p={0} m={0} mt="20px">
                                    {({ isHovered }) => {
                                        return (
                                            <Text
                                                color={'#1264A3'}
                                                fontSize="18px"
                                                fontFamily="Segoe UI"
                                                fontWeight="500"
                                                textDecorationLine={isHovered ? 'underline' : "none"}
                                                key={i}
                                                textAlign="left">
                                                {el.text}
                                            </Text>
                                        )
                                    }}</Pressable>
                            </Link>
                        })}
                    </VStack>
                    <VStack w="50%">
                        {paragraphs.map((text, i) => {
                            // Check if this is security_11 to render as bullet points
                            const isSecurity11 = text.text === t('security_11');
                            
                            if (isSecurity11) {
                                const bulletPoints = text.text.split('\n').filter(point => point.trim() !== '');
                                return (
                                    <div key={i} id={text.text} style={{width: '100%', marginTop: '20px'}}>
                                        <VStack space={2}>
                                            {bulletPoints.map((point, idx) => (
                                                <HStack key={idx} alignItems="flex-start">
                                                    <Text color="#000" fontSize="20px" mr="10px">•</Text>
                                                    <Text
                                                        w="100%"
                                                        color="#000"
                                                        fontSize="20px"
                                                        textAlign="left"
                                                        flex={1}>
                                                        {renderTextWithLinks(point.trim())}
                                                    </Text>
                                                </HStack>
                                            ))}
                                        </VStack>
                                    </div>
                                );
                            }
                            
                            return (
                                <div key={i} id={text.text} style={{width: '100%', marginTop: text.type === 'text' ? '20px' : '40px'}}>
                                    <Text
                                        w="100%"
                                        italic={text.type === 'text' ? false : true}
                                        bold={text.type === 'text' ? false : true}
                                        color={text.type === 'text' ? "#000" : '#293d8b'}
                                        fontSize={text.type === 'text' ? '20px'  : '35px'}
                                        key={i}
                                        textAlign="left">
                                        {renderTextWithLinks(text.text)}
                                    </Text>
                                </div>
                            );
                        })}
                    </VStack>
                </HStack>
            </Box>

            {/* <Box bg={'#fbfbfb'} py={20} maxWidth={768} mx={'auto'}>
                {paragraphs.map((text, i) => {
                    return (
                        <Text
                            bold={text.type === 'text' ? false : true}
                            color={'#555'}
                            fontSize={text.type === 'text' ? 'xl' : '2xl'}
                            key={i}
                            mb={text.type === 'text' ? 12 : 2}
                            px={12}
                            textAlign="left">
                            {text.text}
                        </Text>
                    );
                })}
            </Box> */}
            <Box width="100%" mt="40px" display="flex" flexDir="row" justifyContent="center" backgroundColor="#f6f6f6" pt="40px" position={window.innerWidth < 1920 ? "relative" : undefined}>
                <Footer />
            </Box>
        </Box>
    )
}